import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * Premium photo gallery with lightbox.
 * Props:
 *  - images: string[]   list of image URLs
 *  - eyebrow?: string
 *  - title?: ReactNode
 *  - subtitle?: string
 *  - testIdPrefix?: string
 */
export default function PhotoGallery({ images = [], eyebrow, title, subtitle, testIdPrefix = "gallery", altPrefix = "Réalisation My Animation" }) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!images.length) return null;

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto" data-testid={`${testIdPrefix}-section`}>
      {(eyebrow || title || subtitle) && (
        <div className="mb-12 reveal">
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
          {title && (
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
              {title}
            </h2>
          )}
          {subtitle && <p className="text-white/65 mt-4 max-w-2xl">{subtitle}</p>}
        </div>
      )}

      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
        {images.map((src, i) => (
          <button
            key={src}
            type="button"
            data-testid={`${testIdPrefix}-item-${i}`}
            onClick={() => setActive(src)}
            className="block mb-4 md:mb-6 break-inside-avoid w-full text-left group relative overflow-hidden card-luxe !p-0"
            aria-label={`Agrandir la photo ${i + 1}`}
          >
            <img
              src={src}
              alt={`${altPrefix} — photo ${i + 1}`}
              loading="lazy"
              className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-3 right-4 text-[10px] uppercase tracking-[0.25em] text-[#DCB457] opacity-0 group-hover:opacity-100 transition-opacity">
              Agrandir
            </div>
          </button>
        ))}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setActive(null)}
          data-testid={`${testIdPrefix}-lightbox`}
        >
          <button
            type="button"
            data-testid={`${testIdPrefix}-lightbox-close`}
            className="absolute top-6 right-6 text-white/80 hover:text-[#DCB457] z-10"
            onClick={() => setActive(null)}
            aria-label="Fermer"
          >
            <X size={28} strokeWidth={1.2} />
          </button>
          <img
            src={active}
            alt={`${altPrefix} — vue agrandie`}
            loading="lazy"
            decoding="async"
            className="max-h-[90vh] max-w-[94vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
